import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import classNames from 'classnames';

import { Icon } from '@iconik-io/uad';

import { ICONS } from '../../icons/icon';
import type { FCC } from '../../types/common';
import Button from '../../ui/button/button';
import IconButton from '../../ui/icon_button/icon_button';
import { ui } from '../../utils/helpers';
import ErrorBoundary from '../error_boundary/error_boundary';
import HelpButton from '../help_button/help_button';
import type { PanelAction, PanelSide } from '../panels/types';

import styles from './panel.module.css';

type Props = {
  panelId: string;
  title: string;
  color?: string;
  canClose?: boolean;
  canMaximize?: boolean;
  className?: string;
  onClose?: (id: string) => void;
  helpButton?: { path: string; title: string };
  actions?: PanelAction[];
  side: PanelSide;
};

const Panel: FCC<Props> = (props) => {
  const {
    panelId,
    title,
    children,
    color,
    canClose = true,
    canMaximize = false,
    actions = [],
    className,
    onClose,
    helpButton,
    side,
  } = props;

  const [isMaximized, setIsMaximized] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const toggleMaximized = () => {
    if (!document.startViewTransition) {
      setIsMaximized((previous) => !previous);
      return;
    }

    document.startViewTransition(() => {
      flushSync(() => {
        setIsMaximized((previous) => !previous);
      });
    });
  };

  return (
    <ErrorBoundary>
      <div
        className={classNames(className, styles.root, {
          [styles.fade_left]: side === 'right',
          [styles.fade_right]: side === 'left',
          [styles.maximized]: isMaximized,
        })}
        ref={rootRef}
        role="complementary"
        aria-labelledby={`panel_title_${panelId}`}
        style={
          canMaximize ? { viewTransitionName: `panel-${panelId}` } : undefined
        }
        {...ui({ panel: panelId })}
      >
        <div
          className={styles.heading}
          style={{
            border: !color ? 'none' : undefined,
            borderTopColor: color,
          }}
        >
          <h2 className={styles.title} id={`panel_title_${panelId}`}>
            {title}
          </h2>
          {helpButton ? (
            <HelpButton path={helpButton.path} title={helpButton.title} />
          ) : null}
          <div className={styles.controls}>
            {actions.map((action) => (
              <Button
                key={action.label}
                onClick={() => action.action()}
                className={styles.control}
                size="s"
                noBorder
                variant="info"
              >
                {action.iconName && (
                  <Icon
                    name={action.iconName}
                    className={styles.icon}
                    size="xs"
                  />
                )}
                {action.label}
              </Button>
            ))}
            {canMaximize ? (
              <IconButton
                variant="white"
                icon={isMaximized ? ICONS.Minimize : ICONS.Expand}
                aria-label={isMaximized ? 'Restore panel' : 'Maximize panel'}
                className={styles.maximize_button}
                onClick={toggleMaximized}
              />
            ) : null}
            {canClose ? (
              <IconButton
                variant="white"
                iconName="close"
                className={styles.close_button}
                onClick={() => onClose?.(panelId)}
              />
            ) : null}
          </div>
        </div>
        <div className={styles.content} id={`panel_${panelId}`}>
          {children}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Panel;
